import React, { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { StudentInfo } from '../../../../hooks/useBreakdownDataLicensingFee'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import { LedgerCell, LedgerRow } from '../../../Table/LedgerTable'
import { BreakdownForPDF } from './BreakdownForPDF'
import { borderDashed, fontFamily, styles } from './licensingBillingPdfStyles'
import { LicensingPayment } from '../../../../swagger'
import { dateToDashString } from '../../../../utils/dateUtility'
import { TFunction } from 'i18next'
import { colors } from '../../../../stylesheet'
import {convertTwoDecimalStringToBig, formatBigFixedTwoDecimals, getBigZero} from "../../../../utils/bigJSHelper";

export enum BreakDownLedgerPDFVariants {
  Balance = 'Balance',
  Normal = 'Normal',
}

export interface NormalVariantProps {
  variant: BreakDownLedgerPDFVariants.Normal
  firstStudent: StudentInfo
  additionalStudent: StudentInfo
  subtotal: string
  headerLabel: string
}

export interface BalanceVariantProps {
  variant: BreakDownLedgerPDFVariants.Balance
  totalOwed: string
  totalPaid: string
  balance: string
  currencyCode: string
  programName: string
  paymentList: LicensingPayment[]
  licensingDue: string
}

interface BreakdownLedgerRowForPDF {
  content?: string
  cssTextProps?: React.CSSProperties
}

interface BuildBreakdownLedgerRowsProps {
  elements: Array<BreakdownLedgerRowForPDF>
  cellsCount?: number
  missingCellsPosition?: 'start' | 'end'
  aligns?: ('left' | 'right')[]
  colSpans?: (number | undefined)[]
}

export interface BreakdownLedgerForPDFStyles {
  subHeaderRowCssProps?: React.CSSProperties
  subHeaderCellCssProps?: React.CSSProperties
  bodyCellCssProps?: React.CSSProperties
  paymentHeaderCellCssProps?: React.CSSProperties
  paymentBodyCellCssProps?: React.CSSProperties
  subTotalCellCssProps?: React.CSSProperties
  nestedDescription?: boolean
  displaySubTotalLabel?: boolean
  balanceLabel?: string
}

export const buildBreakdownLedgerRows = ({
  elements,
  cellsCount = 4,
  missingCellsPosition = 'end',
  aligns,
  colSpans,
}: BuildBreakdownLedgerRowsProps): LedgerRow => {
  while (elements.length < cellsCount) {
    if (missingCellsPosition === 'start') {
      elements.unshift({})
    } else {
      // Default to adding empty elements at the end
      // This is useful when we want to ensure a fixed number of cells
      // even if some data is missing.
      elements.push({})
    }
  }

  const cells: LedgerCell[] = []

  for (let i = 0; i < elements.length; i++) {
    const { content, cssTextProps } = elements[i] ?? {
      content: '',
      cssTextProps: undefined,
    }

    const align = aligns?.[i] ?? (i === 0 ? 'left' : 'right')
    const colSpan = colSpans?.[i] ?? (i === elements.length - 1 ? 2 : undefined) // Last cell spans two columns if needed

    cells.push({
      content: content ?? '',
      align,
      cssTextProps,
      cellCssProps: {
        ...cssTextProps,
      },
      colSpan,
    })
  }

  return { cells }
}

const createPaymentsList = (
  paymentList: LicensingPayment[],
  t: TFunction,
  cssTextProps?: CSSProperties,
  paymentBodyCellCssProps?: CSSProperties
) => [
  buildBreakdownLedgerRows({
    elements: [
      {
        content: t('BreakdownLedger.PDF.SubHeader.Payments', 'Payments'),
        cssTextProps: {
          borderTop: borderDashed,
          color: colors.royalBlue.main,
          fontWeight: 'bold',
          paddingTop: 24,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
      {
        cssTextProps: {
          borderTop: borderDashed,
        },
      },
    ],
    cellsCount: 5,
  }),
  buildBreakdownLedgerRows({
    elements: [
      {
        content: t('BreakdownLedger.PDF.Balance.SubHeader.Date', 'Date'),
        cssTextProps: { ...styles.paymentListHeader, ...cssTextProps },
      },
      {
        content: t(
          'BreakdownLedger.PDF.Balance.SubHeader.Description',
          'Description'
        ),
        cssTextProps: { ...styles.paymentListHeader, ...cssTextProps },
      },
      {
        content: t(
          'BreakdownLedger.PDF.Balance.SubHeader.PaymentMethod',
          'Payment Method'
        ),
        cssTextProps: { ...styles.paymentListHeader, ...cssTextProps },
      },
      {
        content: t('BreakdownLedger.PDF.Balance.SubHeader.Status', 'Status'),
        cssTextProps: { ...styles.paymentListHeader, ...cssTextProps },
      },
      {
        content: t('BreakdownLedger.PDF.Balance.SubHeader.Amount', 'Amount'),
        cssTextProps: { ...styles.paymentListHeader, ...cssTextProps },
      },
    ],
    cellsCount: 5,
  }),
  ...paymentList.map((payment) =>
    buildBreakdownLedgerRows({
      elements: [
        {
          content: dateToDashString(payment.begunAt),
          cssTextProps: paymentBodyCellCssProps ?? {
            borderBottom: borderDashed,
          },
        },
        {
          content: payment.desc,
          cssTextProps: paymentBodyCellCssProps ?? {
            borderBottom: borderDashed,
          },
        },
        {
          content: payment.paymentMethodKey,
          cssTextProps: paymentBodyCellCssProps ?? {
            borderBottom: borderDashed,
          },
        },
        {
          content: payment.paymentStatusKey,
          cssTextProps: paymentBodyCellCssProps ?? {
            borderBottom: borderDashed,
          },
        },
        {
          content: `$ ${getLocaleCurrencyForAmount(
            payment.amount.amount,
            payment.amount.currencyCode
          )}`,
          cssTextProps: paymentBodyCellCssProps ?? {
            borderBottom: borderDashed,
          },
        },
      ],
      cellsCount: 5,
    })
  ),
  buildBreakdownLedgerRows({
    elements: [
      {
        content: t('BreakdownLedger.PDF.BalanceRow.SubTotal', 'Sub Total'),
        cssTextProps: paymentBodyCellCssProps ?? {
          borderBottom: borderDashed,
        },
      },
      {},
      {},
      {},
      {
        content: `${getLocaleCurrencyForAmount(
            formatBigFixedTwoDecimals(paymentList.reduce(
                (amountAccumulator, current) =>
                    amountAccumulator.plus(current.amount.amount),
                getBigZero()
            )),
          paymentList[0].amount.currencyCode
        )}`,
        cssTextProps: {
          borderTop: '.20em solid',
        },
      },
    ],
    cellsCount: 5,
  }),
]

export const BreakdownLedgerForPDF: React.FC<
  (NormalVariantProps | BalanceVariantProps) & BreakdownLedgerForPDFStyles
> = (props) => {
  const { t } = useTranslation()

  let rows: LedgerRow[] = []
  let label = ''

  if (props.variant === BreakDownLedgerPDFVariants.Balance) {
    const balanceLabelCell = {
      content:
        props.balanceLabel ||
        t('BreakdownLedger.PDF.BalanceRow.Balance', 'Balance for {{program}}', {
          program: props.programName,
        }),
      cssTextProps: {
        borderTop: borderDashed,
        fontWeight: 'bold',
        fontFamily: fontFamily,
      },
    }

    rows = [
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.BalanceRow.TotalOwed',
              'Total Owed'
            ),
          },
          {},
          {},
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.totalOwed,
              props.currencyCode
            )}`,
          },
        ],
      }),
      ...(props.paymentList.length > 0
        ? createPaymentsList(
            props.paymentList,
            t,
            props.paymentHeaderCellCssProps || {},
            props.paymentBodyCellCssProps
          )
        : []),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.BalanceRow.TotalPaid',
              'Total Paid'
            ),
          },
          {},
          {},
          {
            content: `${
              convertTwoDecimalStringToBig(props.totalPaid).gt(0) ? '-' : ''
            } $ ${getLocaleCurrencyForAmount(
              props.totalPaid,
              props.currencyCode
            )}`,
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          balanceLabelCell,
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.balance,
              props.currencyCode
            )}`,
            cssTextProps: {
              fontWeight: 'bold',
              fontFamily: fontFamily,
              borderTop: borderDashed,
              ...props.subTotalCellCssProps,
            },
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.BalanceRow.LicensingDue',
              'Licensing Due'
            ),
            cssTextProps: {
              borderTop: borderDashed,
              fontWeight: 'bold',
              fontFamily: fontFamily,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            cssTextProps: {
              borderTop: borderDashed,
            },
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.licensingDue,
              props.currencyCode
            )}`,
            cssTextProps: {
              fontWeight: 'bold',
              fontFamily: fontFamily,
              borderTop: borderDashed,
            },
          },
        ],
        cellsCount: 5,
      }),
    ]
  } else {
    const subTotalCell = {
      content: `$ ${getLocaleCurrencyForAmount(
        props.subtotal,
        props.firstStudent.currencyCode
      )}`,
      cssTextProps: {
        fontWeight: 'bold',
        fontFamily: fontFamily,
        borderTop: borderDashed,
        ...props.subTotalCellCssProps,
      },
    }

    rows = [
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Row.FirstStudents',
              'First Students'
            ),
            cssTextProps: {
              ...(props.nestedDescription
                ? {
                    paddingLeft: 32,
                  }
                : {}),
            },
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.firstStudent.feeAmount,
              props.firstStudent.currencyCode
            )}`,
          },
          { content: `${props.firstStudent.numberOfStudents}` },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.firstStudent.studentInfoSubTotal,
              props.firstStudent.currencyCode
            )}`,
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: [
          {
            content: t(
              'BreakdownLedger.PDF.Row.AdditionalStudents',
              'Additional Students'
            ),
            cssTextProps: {
              ...(props.nestedDescription
                ? {
                    paddingLeft: 32,
                  }
                : {}),
            },
          },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.additionalStudent.feeAmount,
              props.additionalStudent.currencyCode
            )}`,
          },
          { content: `${props.additionalStudent.numberOfStudents}` },
          {
            content: `$ ${getLocaleCurrencyForAmount(
              props.additionalStudent.studentInfoSubTotal,
              props.additionalStudent.currencyCode
            )}`,
          },
        ],
      }),
      buildBreakdownLedgerRows({
        elements: !props.displaySubTotalLabel
          ? [subTotalCell]
          : [
              {
                content: t('BreakdownLedger.PDF.Row.SubTotal', 'Sub Total'),
                cssTextProps: {
                  fontWeight: 'bold',
                  fontFamily: fontFamily,
                  borderTop: borderDashed,
                },
              },
              {
                cssTextProps: {
                  borderTop: borderDashed,
                },
              },
              {
                cssTextProps: {
                  borderTop: borderDashed,
                },
              },
              subTotalCell,
            ],
        missingCellsPosition: 'start',
      }),
    ]
    label = props.headerLabel
  }

  return (
    <BreakdownForPDF
      breakdownRows={rows}
      headerLabel={label}
      subHeaderCellCssProps={props.subHeaderCellCssProps}
      subHeaderRowCssProps={props.subHeaderRowCssProps}
      bodyCellCssProps={props.bodyCellCssProps}
    />
  )
}